import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, XCircle, Landmark, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const STATUS_LABEL = {
  no_credential: 'Not seeded',
  key_missing: 'Key missing',
  auth_error: 'Auth error',
  no_accounts: 'No accounts found',
  ok: 'OK',
};

const STATUS_VARIANT = {
  no_credential: 'outline',
  key_missing: 'destructive',
  auth_error: 'destructive',
  no_accounts: 'outline',
  ok: 'default',
};

function EntityRow({ entity, result, loading, onDiscover }) {
  const [expanded, setExpanded] = useState(false);

  const handleDiscover = (accountId) => {
    setExpanded(true);
    onDiscover(entity.entity_id, accountId);
  };

  return (
    <div className="border rounded-lg">
      <div
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30"
        onClick={() => setExpanded(!expanded)}
        data-testid="pl-bank-discovery-row"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        <span className="text-sm font-medium flex-1">{entity.canonical_name}</span>
        <code className="text-xs text-muted-foreground">{entity.entity_id}</code>
        {result && (
          <Badge variant={STATUS_VARIANT[result.status] || 'outline'} className="text-[10px]">
            {STATUS_LABEL[result.status] || result.status}
          </Badge>
        )}
      </div>

      {expanded && (
        <div className="border-t px-4 py-3 space-y-3 bg-muted/10">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleDiscover(null)}
            disabled={loading}
            data-testid="pl-bank-discovery-run"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Search className="h-3.5 w-3.5 mr-1.5" />}
            Discover bank accounts
          </Button>

          {result && result.error && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {result.error}
            </div>
          )}

          {result && result.error_body && (
            <pre className="text-[10px] border rounded p-2 bg-red-50 text-red-800 overflow-x-auto">
              {JSON.stringify(result.error_body, null, 2)}
            </pre>
          )}

          {result && result.accounts && result.accounts.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                {result.accounts.length} bank account(s) discovered:
              </p>
              {result.accounts.map((acc) => (
                <div
                  key={acc.pennylane_account_id}
                  className={`rounded border px-3 py-2 flex items-center justify-between gap-2 ${
                    result.sampled_account_id === acc.pennylane_account_id ? 'border-blue-400 bg-blue-50/40' : ''
                  }`}
                >
                  <div>
                    <span className="text-sm font-medium">{acc.name || '(unnamed)'}</span>
                    <span className="text-xs text-muted-foreground ml-2 font-mono">{acc.iban_masked || 'no IBAN'}</span>
                    <span className="text-xs text-muted-foreground ml-2">{acc.currency}</span>
                    <code className="text-[10px] text-muted-foreground ml-2">id={acc.pennylane_account_id}</code>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDiscover(acc.pennylane_account_id)}
                    disabled={loading}
                  >
                    Sample transactions
                  </Button>
                </div>
              ))}
            </div>
          )}

          {result && result.status === 'no_accounts' && (
            <p className="text-sm text-muted-foreground">
              Credential connected, but Pennylane returned zero bank accounts for this entity.
              Connect a bank inside the Pennylane app first.
            </p>
          )}

          {result && result.transactions_error && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4 shrink-0" />
              {result.transactions_error}
            </div>
          )}

          {result && result.transactions_error_body && (
            <pre
              className="text-[10px] border rounded p-2 bg-red-50 text-red-800 overflow-x-auto"
              data-testid="pl-bank-discovery-transactions-error-body"
            >
              {JSON.stringify(result.transactions_error_body, null, 2)}
            </pre>
          )}

          {result && result.sampled_account_id && result.transactions_sample && result.transactions_sample.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                {result.transactions_sample.length} most recent transaction(s) for account {result.sampled_account_id},
                exactly as returned by the Pennylane API:
              </p>
              <pre className="text-[10px] border rounded p-2 bg-muted/30 overflow-x-auto max-h-96 overflow-y-auto">
                {JSON.stringify(result.transactions_sample, null, 2)}
              </pre>
            </div>
          )}

          {result && result.sampled_account_id && result.transactions_sample
            && result.transactions_sample.length === 0 && !result.transactions_error && (
            <p className="text-sm text-muted-foreground" data-testid="pl-bank-discovery-zero-tx">
              Query succeeded, but Pennylane returned zero transactions for account {result.sampled_account_id}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function PennylaneBankFeedDiscoveryPanel() {
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [results, setResults] = useState({});
  const [discoveringEntity, setDiscoveringEntity] = useState(null);

  const loadEntities = async () => {
    setLoadingEntities(true);
    try {
      const { data } = await diagnosticsAPI.pennylaneEntities();
      setEntities(data.entities || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingEntities(false);
    }
  };

  useEffect(() => { loadEntities(); }, []);

  const runDiscover = async (entityId, accountId) => {
    setDiscoveringEntity(entityId);
    try {
      const { data } = await diagnosticsAPI.pennylaneBankFeedDiscovery(entityId, accountId);
      setResults((prev) => ({ ...prev, [entityId]: data }));
      if (data.status === 'ok') {
        toast.success(`${entityId}: discovery succeeded`);
      } else {
        toast.error(`${entityId}: ${data.error || data.status}`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDiscoveringEntity(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Landmark className="h-5 w-5" />
          Pennylane Bank Feed Discovery
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only. Lists the bank accounts Pennylane exposes via its own bank
          synchronization for each Pennylane-connected entity, and pulls a raw
          sample of the 5 most recent transactions for a selected account.
          Use this to confirm Banque BCP shows up after connecting it inside Pennylane.
        </p>

        {loadingEntities ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading entities...
          </div>
        ) : entities.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Pennylane entities found. Check ENTITY_DEFS in config.py.
          </p>
        ) : (
          <div className="space-y-2" data-testid="pl-bank-discovery-list">
            {entities.map((entity) => (
              <EntityRow
                key={entity.entity_id}
                entity={entity}
                result={results[entity.entity_id]}
                loading={discoveringEntity === entity.entity_id}
                onDiscover={runDiscover}
              />
            ))}
          </div>
        )}

        <Button variant="outline" size="sm" onClick={loadEntities} disabled={loadingEntities}>
          {loadingEntities ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Refresh entity list
        </Button>
      </CardContent>
    </Card>
  );
}
